import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { documentKeys } from './Documents.queries';
import { useTranslation } from '../i18n';

// STD-015: write/imperative actions for the Documents domain. List-changing
// mutations invalidate the list so the grid refreshes automatically. Errors are
// toasted by the global MutationCache handler; meta.errorMessage is the fallback.
export interface UploadDocumentInput {
  file: File;
  title: string;
  description: string | null;
  documentType: string;
  validityStartDate: string | null;
  validityEndDate: string | null;
}

export function useUploadDocument() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('documents.failedToUpload') },
    mutationFn: async (input: UploadDocumentInput) => {
      const res = await apiService.uploadDocument(
        input.file,
        input.title,
        input.description,
        input.documentType,
        input.validityStartDate,
        input.validityEndDate
      );
      if (!res.success) throw new Error(res.error || 'Failed to upload document');
      return res.document;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentKeys.list() }),
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('documents.failedToDelete') },
    mutationFn: async (documentId: string) => {
      const res = await apiService.deleteDocument(documentId);
      if (!res.success) throw new Error(res.error || 'Failed to delete document');
      return res;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: documentKeys.list() }),
  });
}

// Download returns a short-lived presigned URL — an imperative action, kept here
// so the component never imports apiService directly (STD-016).
export function useDownloadDocument() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('documents.failedToDownload') },
    mutationFn: async (documentId: string) => {
      const res = await apiService.downloadDocument(documentId);
      if (!res.success || !res.download_url) {
        throw new Error(res.error || 'Failed to generate download URL');
      }
      return res.download_url;
    },
  });
}
